import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type ActionPageMode = 'create' | 'edit' | 'view' | 'clone' | 'supervisor-approval';

export interface UseActionsBreadcrumbsOptions {
  mode: ActionPageMode;
  /** The action item (for edit/view/clone modes) */
  item?: {id?: number; subject?: string};
}

interface UseActionsBreadcrumbsResult {
  backBarProps: {fallbackTo: string; breadcrumbs: BreadcrumbItem[]};
}

/**
 * Hook to generate consistent BackBar props for Actions pages under the Manage Actions tab.
 *
 * All action detail pages (view/edit/clone/create) are under the 'manage' path,
 * so breadcrumbs link back to Manage Actions tab.
 */
export const useActionsBreadcrumbs = ({
  mode,
  item,
}: UseActionsBreadcrumbsOptions): UseActionsBreadcrumbsResult => {
  const backBarProps = useMemo(() => {
    const manageActionsPath = PathUtility.Actions('manage');

    const breadcrumbs: BreadcrumbItem[] = [
      {label: 'Manage Actions', to: manageActionsPath},
    ];

    switch (mode) {
      case 'create':
        breadcrumbs.push({label: 'Create Action'});
        break;

      case 'edit':
        if (item?.id) {
          breadcrumbs.push({
            label: item.subject || `Action #${item.id}`,
            to: PathUtility.ActionView(String(item.id), 'content'),
          });
        }
        breadcrumbs.push({label: 'Edit'});
        break;

      case 'view':
        breadcrumbs.push({label: item?.subject || 'View Action'});
        break;

      case 'clone':
        if (item?.id) {
          breadcrumbs.push({
            label: item.subject || `Action #${item.id}`,
            to: PathUtility.ActionView(String(item.id), 'content'),
          });
        }
        breadcrumbs.push({label: 'Clone'});
        break;

      case 'supervisor-approval':
        breadcrumbs[0] = {
          label: 'Supervisor Approvals',
          to: PathUtility.Actions('supervisor-approval'),
        };
        breadcrumbs.push({label: 'Review Approval'});
        break;
    }

    // Contextual back navigation:
    // - edit/clone: go back to the view page of the item
    // - supervisor-approval: go back to the supervisor approvals list
    // - create/view: go back to Manage Actions list
    let fallbackTo: string;
    if ((mode === 'edit' || mode === 'clone') && item?.id) {
      fallbackTo = PathUtility.ActionView(String(item.id), 'content');
    } else if (mode === 'supervisor-approval') {
      fallbackTo = PathUtility.Actions('supervisor-approval');
    } else {
      fallbackTo = manageActionsPath;
    }

    return {fallbackTo, breadcrumbs};
  }, [mode, item?.id, item?.subject]);

  return {backBarProps};
};
